import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Paper, Button, Grid, Divider, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import API from "../API/API";

const api = new API();

function SummaryCard({ title, data, color }) {
  const fields = [
    { key: "cash", label: "Cash" },
    { key: "upi", label: "UPI" },
    { key: "card", label: "Card" },
    { key: "credit", label: "Credit" },
    { key: "bank", label: "Bank" },
    { key: "cheque", label: "Cheque" },
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        p: 2,
        bgcolor: "#0e1729",
        border: `1px solid ${color}44`,
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: color,
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: 1,
          display: "block",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        {fields.map((f) => (
          <Grid item xs={4} key={f.key}>
            <Box sx={{ textAlign: "center", p: 0.5, bgcolor: "#1a2035", borderRadius: 1 }}>
              <Typography sx={{ color: "#8faac8", fontSize: "9px" }}>{f.label}</Typography>
              <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: "bold" }}>
                {data?.[f.key] || "0.00"}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ bgcolor: "#1c2b4d", mb: 1 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 0.5 }}>
        <Box>
          <Typography sx={{ color: "#8faac8", fontSize: "10px" }}>Refund</Typography>
          <Typography sx={{ color: "#ff3333", fontSize: "13px", fontWeight: "bold" }}>
            {data?.refund || "0.00"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ color: color, fontSize: "11px", fontWeight: "bold" }}>TOTAL</Typography>
          <Typography sx={{ color: "#ffd700", fontSize: "20px", fontWeight: "bold" }}>
            ₹{data?.total || "0.00"}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Dashboard() {
  const userString = localStorage.getItem("user");
  let user = null;
  try {
    if (userString) user = JSON.parse(userString);
  } catch (e) {}

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoadingSummary(true);
      try {
        const dateStr = dayjs().format("YYYY-MM-DD");
        const res = await api.get(`api/day-amount-transactions/summary?date=${dateStr}`);
        setSummary(res?.data || null);
      } catch (e) {
        console.error("Failed to fetch summary", e);
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchSummary();
  }, []);

  const navItems = [
    { label: "Guest Images", to: "/guest-images", color: "#4B90FC" },
    { label: "Day Transactions", to: "/day-amount-transactions", color: "#9c27b0" },
    { label: "Advance Report", to: "/advance-data-report", color: "#0288d1" },
    { label: "Room Ledger", to: "/room-ledger-report", color: "#008080" },
  ];

  return (
    <Box sx={{ bgcolor: "#111C31", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 3, p: 2.5, bgcolor: "#0e1729", borderRadius: 2, border: "1px solid #1c2b4d" }}>
        <Typography variant="h5" sx={{ color: "#4B90FC", fontWeight: "bold" }}>
          🏨 Hotel Management Dashboard
        </Typography>
        <Typography sx={{ color: "#8faac8", mt: 0.5, fontSize: "14px" }}>
          Welcome back, {user?.name || user?.username || "Admin"}! Today is {dayjs().format("dddd, DD MMM YYYY")}.
        </Typography>
      </Box>

      {/* Today & This Month Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: "13px", mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
          📊 Transaction Summary
        </Typography>
        {loadingSummary ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#4B90FC" }} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SummaryCard title="Today's Summary" data={summary?.today} color="#4B90FC" />
            </Grid>
            <Grid item xs={12} md={6}>
              <SummaryCard title="This Month's Summary" data={summary?.this_month} color="#9c27b0" />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box>
        <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: "13px", mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
          🔗 Quick Access
        </Typography>
        <Grid container spacing={2}>
          {navItems.map((item) => (
            <Grid item xs={6} md={3} key={item.to}>
              <Button
                fullWidth
                variant="contained"
                component={Link}
                to={item.to}
                sx={{
                  bgcolor: item.color,
                  py: 2,
                  fontWeight: "bold",
                  fontSize: "13px",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: `0 4px 15px ${item.color}44`,
                  "&:hover": {
                    bgcolor: item.color,
                    opacity: 0.85,
                    transform: "translateY(-2px)",
                    transition: "all 0.2s",
                  },
                }}
              >
                {item.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
